import React, { useState, useEffect } from 'react';
import { X, Sparkles } from 'lucide-react';

const POPUP_DISMISSED_KEY = 'skilledge_popup_dismissed';
const POPUP_DISMISS_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

const LandingPagePopup = ({ settings, onOpenChat }) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (!settings || !settings.enabled) {
      return;
    }

    // Check if popup was recently dismissed
    const dismissedAt = localStorage.getItem(POPUP_DISMISSED_KEY);
    if (dismissedAt) {
      const dismissedTime = parseInt(dismissedAt, 10);
      const now = Date.now();
      // If dismissed less than 24 hours ago, don't show
      if (now - dismissedTime < POPUP_DISMISS_DURATION) {
        return;
      }
    }

    // Show popup after delay
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, (settings.delay_seconds || 3) * 1000);

    return () => clearTimeout(timer);
  }, [settings]);

  if (!settings || !settings.enabled || !isVisible) {
    return null;
  }

  const handleClose = () => {
    // Save dismissal time to localStorage
    localStorage.setItem(POPUP_DISMISSED_KEY, Date.now().toString());
    setIsVisible(false);
  };

  const handleButtonClick = () => {
    // Save dismissal time to localStorage
    localStorage.setItem(POPUP_DISMISSED_KEY, Date.now().toString());
    setIsVisible(false);
    
    // Always open chat popup for any chat-related link
    if (settings.button_link && (
      settings.button_link === '/chatbot' || 
      settings.button_link === '/chat' || 
      settings.button_link === '/ai' ||
      settings.button_link.toLowerCase().includes('chat')
    )) {
      // Trigger chat popup
      if (onOpenChat) {
        onOpenChat();
      }
    } else if (settings.button_link) {
      // Navigate to other links
      if (settings.button_link.startsWith('http')) {
        window.open(settings.button_link, '_blank');
      } else {
        window.location.href = settings.button_link;
      }
    } else {
      // Default: open chat if no link specified
      if (onOpenChat) {
        onOpenChat();
      }
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9998] animate-fade-in"
        onClick={handleClose}
      />

      {/* Popup Modal */}
      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 pointer-events-none">
        <div 
          className="relative w-full max-w-3xl rounded-2xl shadow-2xl overflow-hidden animate-scale-in pointer-events-auto"
          style={{ backgroundColor: settings.background_color || '#0f172a' }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close Button */}
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 z-10 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-all duration-200"
            aria-label="Close popup"
          >
            <X className="w-5 h-5 text-white" />
          </button>

          {/* Content Grid */}
          <div className="grid md:grid-cols-2 gap-6 p-8 md:p-12">
            {/* Left Side - Text Content */}
            <div className="flex flex-col justify-center space-y-6 text-white">
              {/* Badge */}
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-cyan-400/30 bg-cyan-400/10 w-fit">
                <Sparkles className="w-4 h-4" style={{ color: settings.primary_color || '#38bdf8' }} />
                <span className="text-sm font-medium" style={{ color: settings.primary_color || '#38bdf8' }}>
                  {settings.title || 'Spark AI'}
                </span>
              </div>

              {/* Title */}
              <div>
                <h2 className="text-3xl md:text-4xl font-bold mb-3 leading-tight">
                  {settings.subtitle || 'Your Personal Career Guidance Buddy'}
                </h2>
                <p className="text-gray-300 text-lg">
                  {settings.description || 'Ask Spark to answer all your career-related queries'}
                </p>
              </div>

              {/* Features List */}
              <div className="space-y-3">
                {settings.features && settings.features.length > 0 ? (
                  settings.features.map((feature, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <div 
                        className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: `${settings.primary_color || '#38bdf8'}20` }}
                      >
                        <Sparkles 
                          className="w-3 h-3" 
                          style={{ color: settings.primary_color || '#38bdf8' }} 
                        />
                      </div>
                      <span className="text-gray-200">{feature}</span>
                    </div>
                  ))
                ) : (
                  <>
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: `${settings.primary_color || '#38bdf8'}20` }}
                      >
                        <Sparkles 
                          className="w-3 h-3" 
                          style={{ color: settings.primary_color || '#38bdf8' }} 
                        />
                      </div>
                      <span className="text-gray-200">Best Fit Advice</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: `${settings.primary_color || '#38bdf8'}20` }}
                      >
                        <Sparkles 
                          className="w-3 h-3" 
                          style={{ color: settings.primary_color || '#38bdf8' }} 
                        />
                      </div>
                      <span className="text-gray-200">Instant Career Insights</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: `${settings.primary_color || '#38bdf8'}20` }}
                      >
                        <Sparkles 
                          className="w-3 h-3" 
                          style={{ color: settings.primary_color || '#38bdf8' }} 
                        />
                      </div>
                      <span className="text-gray-200">Interactive Career Support</span>
                    </div>
                  </>
                )}
              </div>

              {/* Footer Text */}
              <p className="text-sm text-gray-400 italic">
                Give Your Career Journey an AI-Powered Edge
              </p>

              {/* CTA Button */}
              <button
                onClick={handleButtonClick}
                className="px-8 py-3 rounded-lg font-semibold text-base shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 w-fit"
                style={{ 
                  backgroundColor: settings.primary_color || '#38bdf8',
                  color: '#ffffff'
                }}
              >
                {settings.button_text || 'Start Now'}
              </button>
            </div>

            {/* Right Side - Image/Illustration */}
            <div className="hidden md:flex items-center justify-center">
              {settings.image_url ? (
                <img 
                  src={settings.image_url} 
                  alt={settings.title}
                  className="w-full h-auto max-w-sm object-contain"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              ) : (
                <div className="w-72 h-72 relative">
                  {/* Animated Background Circles */}
                  <div 
                    className="absolute inset-0 rounded-full opacity-20 animate-pulse"
                    style={{ backgroundColor: settings.primary_color || '#38bdf8' }}
                  />
                  <div 
                    className="absolute inset-8 rounded-full opacity-30 animate-pulse"
                    style={{ 
                      backgroundColor: settings.primary_color || '#38bdf8',
                      animationDelay: '0.5s'
                    }}
                  />
                  
                  {/* Robot/AI Icon */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center">
                      <div 
                        className="w-32 h-32 mx-auto rounded-full flex items-center justify-center mb-4"
                        style={{ backgroundColor: `${settings.primary_color || '#38bdf8'}30` }}
                      >
                        <Sparkles 
                          className="w-16 h-16" 
                          style={{ color: settings.primary_color || '#38bdf8' }}
                        />
                      </div>
                      <div className="space-y-2">
                        <div 
                          className="h-2 w-24 mx-auto rounded-full"
                          style={{ backgroundColor: `${settings.primary_color || '#38bdf8'}40` }}
                        />
                        <div 
                          className="h-2 w-32 mx-auto rounded-full"
                          style={{ backgroundColor: `${settings.primary_color || '#38bdf8'}40` }}
                        />
                        <div 
                          className="h-2 w-20 mx-auto rounded-full"
                          style={{ backgroundColor: `${settings.primary_color || '#38bdf8'}40` }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes fade-in {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes scale-in {
          from {
            opacity: 0;
            transform: scale(0.9);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        .animate-fade-in {
          animation: fade-in 0.3s ease-out;
        }

        .animate-scale-in {
          animation: scale-in 0.3s ease-out;
        }
      `}</style>
    </>
  );
};

export default LandingPagePopup;
